/**
 * File Name          : command-packet.model.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.0.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_BACKGROUNDTECH_LICENSE.txt', which is part of this source code package.
 */

import { PACKET_TYPE, Packet_Abstract, Packet } from "./packet.model";
import { Uint16Bytes, Uint32Bytes } from "../../byte-helpers";
import { ACCESS_CONTROL_ROLE } from "../file_system/access-control-role.model";
import { FILE_ACCESS } from "../file_system/file-access.model";
import { FILE_OPEN_MODE } from "../file_system/file-open-mode.model";

export enum PACKET_SUB_TYPE {
    NA                               = 0x00,
    TEST                             = 0x01,
    SUBSCRIBE_BY_INDEX               = 0x02,
    SUBSCRIBE_BY_ID                  = 0x03,
    GET_PORTS                        = 0x04,
    GET_PORTS_NAME                   = 0x05,
    GET_PUBLISHERS                   = 0x06,
    GET_PUBLISHER_NAME               = 0x07,
    GET_OUTPUTS                      = 0x08,
    GET_OUTPUT_NAME                  = 0x09,
    GET_OUTPUT_UNITS                 = 0x0A,
    GET_SUBSCRIBERS                  = 0x0B,
    GET_SUBSCRIBER_NAME              = 0x0C,
    GET_PORT_STATS                   = 0x0D,
    SELF_TEST                        = 0x0E,
    PORT_UNSUBSCRIBE_ALL             = 0x0F,

    GET_PARAMETER_SETS				= 0x10, // Get a list of all parameter sets
    GET_PARAMETER_SET_INFO			= 0x11, // Get name and other info on a single parameter set
    GET_PARAMETERS					= 0x12, // Get a list of all parameters in a set
    GET_PARAMETER_INFO				= 0x13, // Get name and other info on a single parameter
    GET_PARAMETER_ATTRIBUTES		= 0x14, // Get attributes of a single parameter
    GET_PARAMETER_VAL				= 0x15, // Get the value of a single parameter
    SET_PARAMETER_VAL				= 0x16,	// Get the value of a single parameter - Access controlled per UUID		
    SAVE_PARAMETER					= 0x17,	// Save a single paremeter to file (not yet implemented)
    RESTORE_PARAMETER				= 0x18,	// Restore a single parameter from file - Access controlled per UUID
    SAVE_ALL_PARAMETERS				= 0x19, // Save all CONFIG parameters to file
    RESTORE_ALL_PARAMETERS			= 0x1A, // Restore all CONFIG parameters from file
    SAVE_ALL_PARAMETERS_MFG			= 0x1B, // Save all MANUFACTURING parameters to file
    RESTORE_ALL_PARAMETERS_MFG		= 0x1C, // Restore all MANUFACTURING parameters from file


    GET_TIME                         = 0x20,
    SET_TIME                         = 0x21,
    GET_DATE                         = 0x22,
    SET_DATE                         = 0x23,
    GET_MICROSECONDS                 = 0x24,
    SET_MICROSECONDS                 = 0x25,
    GET_POWER                        = 0x26,
    SET_POWER                        = 0x27,
    GET_BATCHRGSTATE                 = 0x28,
    GET_DEVINFO                      = 0x29,
    GET_DEVNAME                      = 0x2A,
    // RESTART                          = 0x2B,
    IDENTIFY                         = 0x2C,
    TAG_DATA                         = 0x2D,
    ONE_WAY_RANGING					 = 0x2E,
    TWO_WAY_RANGING					 = 0x2F,

    GET_FILERECORDER_STATS           = 0x30,
    ENABLE_FILERECORDER              = 0x31,
    ENABLE_AHRS                      = 0x32,
    DISABLE_FILERECORDER			 = 0x33,
    FOG_DET_DBG						 = 0x34,
    ENABLE_FILERECORDER_V2		     = 0x35, 

    GET_DRIVE_LIST					 = 0x40,
    GET_FILE_LIST					 = 0x41,
    GET_FILE_NAME					 = 0x42,
    GET_FILE_HASH					 = 0x43,
    GET_FILE_DATA					 = 0x44,
    COPY_FILE						 = 0x46,
    MOVE_FILE						 = 0x47,
    EXPORT_FS_IMAGE					 = 0x48,
    IMPORT_FS_IMAGE					 = 0x49,
    DIR_OPEN						 = 0x4A,
    DIR_READ_NEXT					 = 0x4B,
    DIR_CLOSE						 = 0x4C,
    FILE_OPEN						 = 0x4D,
    FILE_CLOSE						 = 0x4E,
    GET_DISK_STATS					 = 0x4F,

    ACTIVATE_ROLE				 	 = 0x50,
		
    REFORMAT_DRIVE					 = 0x55,
    FILE_WRITE                       = 0x56,
    FILE_CHECK_EXISTS                = 0x57,
    FILE_RENAME                      = 0x58,
    FILE_REMOVE                      = 0x59,
    
    GET_BLE_STATUS					 = 0x60,
    ACTIVATE_BLE_NETWORK			 = 0x61,
    CLEAR_BLE_BONDS					 = 0x62,
    
//		START_BLE_PAIRING				= 0x63,
//		CANCEL_BLE_PAIRING				= 0x64,
//		ACCEPT_BLE_PAIRING				= 0x65,
//		REJECT_BLE_PAIRING				= 0x66,
//		GET_BLE_PAIRING_STATUS			= 0x67,
    
    INITIATE_BLE_PAIRING			 = 0x63,
    CANCEL_BLE_PAIRING				 = 0x64,
    ACCEPT_BLE_PAIRING_REQUEST		 = 0x65,
    REJECT_BLE_PAIRING_REQUEST		 = 0x66,

    DFU_GET_STATUS					= 0x70,
    DFU_CONFIRM_IMAGE				= 0x71,
    DFU_TEST_IMAGE					= 0x72,
    DFU_ERASE_IMAGE					= 0x73,
    DFU_LOAD_IMAGE					= 0x74,
    DFU_GET_IMAGE_LIST				= 0x75,
    DFU_GET_IMAGE_INFO				= 0x76,
    DFU_UPLOAD_IMAGE				= 0x77, //Uses file system to transfer file and then perform load to the secondary slot
    

    SET_COLOR                        = 0x80,

    GET_SUBSYSTEM_LIST              = 0xA0,
    GET_SUBSYSTEM_STATUS            = 0xA1,
    GET_SYSTEM_STATUS               = 0xA2,
    
    BAD_PACKET						 = 0xFF,
}

export namespace PACKET_SUB_TYPE {
    export function from(b: number): PACKET_SUB_TYPE | null {
        for (const key in PACKET_SUB_TYPE) {
            if (PACKET_SUB_TYPE[key as keyof typeof PACKET_SUB_TYPE] === b) {
                return PACKET_SUB_TYPE[key as keyof typeof PACKET_SUB_TYPE] as PACKET_SUB_TYPE;
            }
        }
        return null;
    }
}

enum FILE_TYPE {
    BINARY = 0x00,
    ASCII = 0x01
}

export class CommandPacket extends Packet {
    override pktSubType: PACKET_SUB_TYPE | null = null;

    publisherId: number | null = null;
    outputIdx: number | null = null;
    destinationPortID: number | null = null;
    subscriberType: number | null = null;
    downSample: number | null = null;
    fileName: string | null = null;
    fileType: FILE_TYPE | null = null;
    paramSetUUID: number | null = null;
    paramUUID: number | null = null;
    paramVal: Uint8Array | null = null;
    powerLevel: number | null = null;
    testLevel: number | null = null;
    portId: number | null = null;
    enableRecorder: boolean | null = null;
    recordName: string | null = null;
    timeoutMs: number | null = null;
    timeoutFilesize: number | null = null;
    timeoutAction: number | null = null;
    driveIdx: number | null = null;
    weekday: number | null = null;
    year: number | null = null;
    month: number | null = null;
    day: number | null = null;
    hour: number | null = null;
    minute: number | null = null;
    second: number | null = null;
    fracSeconds: number | null = null;
    tagName: string | null = null;
    markerTimeUs: number | null = null;
    ahrsID: number | null = null;
    ahrsState: number | null = null;
    accessControlRole: ACCESS_CONTROL_ROLE | null = null;
    password: string | null = null;
    fileAccessType: FILE_ACCESS | null = null;
    fileAccessMode: FILE_OPEN_MODE | null = null;
    filePath: string | null = null;
    dirPath: string | null = null;
    fileIdx: number | null = null;
    controlFlags: number | null = null;
    chunkHash: number | null = null;
    fileData: Uint8Array | null = null;
    numBytesToHash: number | null = null;
    maxRxSize: number | null = null;
    startAddress: number | null = null;
    led_pattern: {
        red: number;
        green: number;
        blue: number;
        durationUs: number;
        blinkRate: number;
    } | null = null;
    led_color: {
        red: number;
        green: number;
        blue: number;
    } | null = null;
    imageIdx: number | null = null;



    constructor();
    constructor(mPacketNumber: number, pktSubType: PACKET_SUB_TYPE);
    constructor(mPacketNumber?: number, pktSubType?: PACKET_SUB_TYPE) {
        super();
        this.mPacketType = PACKET_TYPE.COMMAND;
        if (pktSubType !== undefined) {
            this.pktSubType = pktSubType;
        }
        if (mPacketNumber !== undefined) {
            this.mPacketNumber = mPacketNumber;
        }
    }

    override packetSubTypeAsString(): string {
        switch(this.pktSubType) {
            case PACKET_SUB_TYPE.NA: return "NA"
            case PACKET_SUB_TYPE.TEST: return "Test"
            case PACKET_SUB_TYPE.SUBSCRIBE_BY_INDEX: return "Subscribe by Index"
            case PACKET_SUB_TYPE.SUBSCRIBE_BY_ID: return "Subscribe by ID"
            case PACKET_SUB_TYPE.GET_PORTS: return "Get Ports"
            case PACKET_SUB_TYPE.GET_PORTS_NAME: return "Get Ports Name"
            case PACKET_SUB_TYPE.GET_PUBLISHERS: return "Get Publishers"
            case PACKET_SUB_TYPE.GET_PUBLISHER_NAME: return "Get Publisher Name"
            case PACKET_SUB_TYPE.GET_OUTPUTS: return "Get Outputs"
            case PACKET_SUB_TYPE.GET_OUTPUT_NAME: return "Get Output Name"
            case PACKET_SUB_TYPE.GET_OUTPUT_UNITS: return "Get Output Units"
            case PACKET_SUB_TYPE.GET_SUBSCRIBERS: return "Get Subscribers"
            case PACKET_SUB_TYPE.GET_SUBSCRIBER_NAME: return "Get Subscriber Name"
            case PACKET_SUB_TYPE.GET_PORT_STATS: return "Get Port Stats"
            case PACKET_SUB_TYPE.SELF_TEST: return "Self Test"
            case PACKET_SUB_TYPE.PORT_UNSUBSCRIBE_ALL: return "Port Unsubscribe All"
        
            case PACKET_SUB_TYPE.GET_PARAMETER_VAL: return "Get Parameter Val"
            case PACKET_SUB_TYPE.SET_PARAMETER_VAL: return "Set Parameter Val"
            case PACKET_SUB_TYPE.GET_PARAMETER_INFO: return "Get Parameter Info"
        
            case PACKET_SUB_TYPE.GET_TIME: return "Get Time"
            case PACKET_SUB_TYPE.SET_TIME: return "Set Time"
            case PACKET_SUB_TYPE.GET_DATE: return "Get Date"
            case PACKET_SUB_TYPE.SET_DATE: return "Set Date"
            case PACKET_SUB_TYPE.GET_MICROSECONDS: return "Get Mircoseconds"
            case PACKET_SUB_TYPE.SET_MICROSECONDS: return "Set Microseconds"
            case PACKET_SUB_TYPE.GET_POWER: return "Get Power"
            case PACKET_SUB_TYPE.SET_POWER: return "Set Power"
            case PACKET_SUB_TYPE.GET_BATCHRGSTATE: return "Get Battery Charge State"
            case PACKET_SUB_TYPE.GET_DEVINFO: return "Get Device Info"
            case PACKET_SUB_TYPE.GET_DEVNAME: return "Get Device Name"
            // case PACKET_SUB_TYPE.RESTART: return "Restart"
            case PACKET_SUB_TYPE.IDENTIFY: return "Identify"
            case PACKET_SUB_TYPE.TAG_DATA: return "Tag Data"
        
            case PACKET_SUB_TYPE.GET_FILERECORDER_STATS: return "Get Filerecorder State"
            case PACKET_SUB_TYPE.ENABLE_FILERECORDER: return "Enable Filerecorder"
            case PACKET_SUB_TYPE.ENABLE_AHRS: return "Enable AHRS"
            default: return "unknown"
        }
    }

    override packetPayloadAsString(): string {
        switch(this.pktSubType) {
            case PACKET_SUB_TYPE.NA: return ""
            case PACKET_SUB_TYPE.TEST: return ""
            case PACKET_SUB_TYPE.SUBSCRIBE_BY_INDEX: return ""
            case PACKET_SUB_TYPE.SUBSCRIBE_BY_ID: return `PubId: ${this.publisherId} \nPort: ${this.destinationPortID} \nSubType: ${this.subscriberType} \nDownSample: ${this.downSample}`
            case PACKET_SUB_TYPE.GET_PORTS: return ""
            case PACKET_SUB_TYPE.GET_PORTS_NAME: return ""
            case PACKET_SUB_TYPE.GET_PUBLISHERS: return ""
            case PACKET_SUB_TYPE.GET_PUBLISHER_NAME: return `PubId: ${  this.publisherId}`
            case PACKET_SUB_TYPE.GET_OUTPUTS: return `PubId: ${  this.publisherId}`
            case PACKET_SUB_TYPE.GET_OUTPUT_NAME: return `PubId: ${  this.publisherId} \nOutputIdx: ${this.outputIdx}`
            case PACKET_SUB_TYPE.GET_OUTPUT_UNITS: return `PubId: ${  this.publisherId} \nOutputIdx: ${this.outputIdx}`
            case PACKET_SUB_TYPE.GET_SUBSCRIBERS: return ""
            case PACKET_SUB_TYPE.GET_SUBSCRIBER_NAME: return ""
            case PACKET_SUB_TYPE.GET_PORT_STATS: return `Port: ${this.portId}`
            case PACKET_SUB_TYPE.SELF_TEST: return ""
            case PACKET_SUB_TYPE.PORT_UNSUBSCRIBE_ALL: return `Port: ${this.portId}`
        
            case PACKET_SUB_TYPE.GET_PARAMETER_VAL: return ""
            case PACKET_SUB_TYPE.SET_PARAMETER_VAL: return ""
            case PACKET_SUB_TYPE.GET_PARAMETER_INFO: return ""
        
            case PACKET_SUB_TYPE.GET_TIME: return ""
            case PACKET_SUB_TYPE.SET_TIME: return `Time: ${this.hour}:${this.minute}:${this.second}:${this.fracSeconds}/256`
            case PACKET_SUB_TYPE.GET_DATE: return ""
            case PACKET_SUB_TYPE.SET_DATE: return `Date: ${this.year}/${this.month}/${this.day} (${this.weekday})`
            case PACKET_SUB_TYPE.GET_MICROSECONDS: return ""
            case PACKET_SUB_TYPE.SET_MICROSECONDS: return ""
            case PACKET_SUB_TYPE.GET_POWER: return ""
            case PACKET_SUB_TYPE.SET_POWER: return `Power level: ${this.powerLevel}`
            case PACKET_SUB_TYPE.GET_BATCHRGSTATE: return ""
            case PACKET_SUB_TYPE.GET_DEVINFO: return ""
            case PACKET_SUB_TYPE.GET_DEVNAME: return ""
            // case PACKET_SUB_TYPE.RESTART: return ""
            case PACKET_SUB_TYPE.IDENTIFY: return ""
            case PACKET_SUB_TYPE.TAG_DATA: return ""
        
            case PACKET_SUB_TYPE.GET_FILERECORDER_STATS: return ""
            case PACKET_SUB_TYPE.ENABLE_FILERECORDER: return ""
            case PACKET_SUB_TYPE.ENABLE_AHRS: return ""
            case PACKET_SUB_TYPE.ACTIVATE_ROLE: return ""
            default: return ""
        }
    }

    override calcBytes(): boolean {
        let packetLen = 2 + this.payloadLenWords(this.pktSubType);

        switch (this.pktSubType) {
            case PACKET_SUB_TYPE.SET_PARAMETER_VAL:
                packetLen += Math.ceil(this.paramVal!.length/4)
                break;
            case PACKET_SUB_TYPE.TAG_DATA:
                packetLen += Math.ceil((6 + this.tagName!.length + 1)/4)
                break;
            case PACKET_SUB_TYPE.ENABLE_FILERECORDER:
                packetLen += Math.ceil((3 + this.recordName!.length)/4)
                break;
            case PACKET_SUB_TYPE.ENABLE_FILERECORDER_V2:
                packetLen += Math.ceil((12 + this.recordName!.length)/4)
                break;
            case PACKET_SUB_TYPE.ACTIVATE_ROLE:
                packetLen += Math.ceil(this.password!.length/4)
                break;
            case PACKET_SUB_TYPE.FILE_OPEN:
                packetLen += Math.ceil(this.filePath!.length / 4);
                break;
            case PACKET_SUB_TYPE.DIR_OPEN:
                packetLen += Math.ceil(this.dirPath!.length / 4);
                break;
            case PACKET_SUB_TYPE.FILE_REMOVE:
                packetLen += Math.ceil(this.filePath!.length / 4);
                break;
            case PACKET_SUB_TYPE.FILE_WRITE:
                packetLen += Math.ceil(this.fileData!.length / 4);
                break;
            case PACKET_SUB_TYPE.DFU_UPLOAD_IMAGE:
                packetLen += Math.ceil(this.fileData!.length / 4);
                break;
        }

        
        const mBytes = new Uint8Array(packetLen * 4).fill(0x00);

        mBytes[0] = 0xCC;
        mBytes[1] = packetLen - 2;

        const packetNumberBytes = Uint16Bytes(this.mPacketNumber!);
        mBytes.set(packetNumberBytes, 2);

        mBytes[4] = this.mPacketType!;
        mBytes[5] = this.pktSubType!;

        switch (this.pktSubType) {
            case PACKET_SUB_TYPE.SET_TIME:
                mBytes[8] = this.fracSeconds!
                mBytes[9] = this.second!
                mBytes[10] = this.minute!
                mBytes[11] = this.hour!
                break;
            case PACKET_SUB_TYPE.SET_DATE:
                mBytes[8] = this.day!
                mBytes[9] = this.month!
                mBytes[10] = this.year!
                mBytes[11] = this.weekday!
                break;
            case PACKET_SUB_TYPE.GET_PUBLISHER_NAME:
                mBytes.set(Uint16Bytes(this.publisherId!),8)
                break;
            case PACKET_SUB_TYPE.GET_OUTPUTS:
                mBytes.set(Uint16Bytes(this.publisherId!),8)
                break;
            case PACKET_SUB_TYPE.GET_OUTPUT_NAME:
                mBytes.set(Uint16Bytes(this.publisherId!),8)
                mBytes[10] = this.outputIdx!
                break;
            case PACKET_SUB_TYPE.GET_OUTPUT_UNITS:
                mBytes.set(Uint16Bytes(this.publisherId!),8)
                mBytes[10] = this.outputIdx!
                break;
            case PACKET_SUB_TYPE.SUBSCRIBE_BY_ID:
                mBytes.set(Uint16Bytes(this.publisherId!),8)
                mBytes.set(Uint16Bytes(this.destinationPortID!),10)
                mBytes[12] = this.subscriberType!
                mBytes.set(Uint32Bytes(this.downSample!),13)
                break;
            case PACKET_SUB_TYPE.PORT_UNSUBSCRIBE_ALL:
                mBytes.set(Uint16Bytes(this.destinationPortID!), 8)
                break;
            case PACKET_SUB_TYPE.SET_POWER:
                mBytes[8] = this.powerLevel!
                break;
            case PACKET_SUB_TYPE.GET_PARAMETER_SET_INFO:
                mBytes.set(Uint16Bytes(this.paramSetUUID!),8)
                break;
            case PACKET_SUB_TYPE.GET_PARAMETERS:
                mBytes.set(Uint16Bytes(this.paramSetUUID!),8)
                break;
            case PACKET_SUB_TYPE.GET_PARAMETER_INFO:
                mBytes.set(Uint32Bytes(this.paramUUID!), 8)
                break;
            case PACKET_SUB_TYPE.GET_PARAMETER_VAL:
                mBytes.set(Uint32Bytes(this.paramUUID!), 8)
                break;
            case PACKET_SUB_TYPE.SET_PARAMETER_VAL:
                mBytes.set(Uint32Bytes(this.paramUUID!), 8)
                mBytes.set(this.paramVal!, 12)
                break;
            case PACKET_SUB_TYPE.TAG_DATA: {
                mBytes.set(Uint16Bytes(this.portId!),8)
                mBytes.set(Uint32Bytes(this.markerTimeUs!),10)

                const encoder = new TextEncoder();
                const tagStrBytes = encoder.encode(this.tagName!)

                mBytes.set(tagStrBytes, 14)
                break;
            }
            case PACKET_SUB_TYPE.ENABLE_FILERECORDER: {
                mBytes.set(Uint16Bytes(this.portId!),8);
                mBytes[10] = this.enableRecorder!?0x01:0x00;

                const encoder = new TextEncoder();
                const recordNameStrBytes = encoder.encode(this.recordName!)
                mBytes.set(recordNameStrBytes, 11)
                break;
            }
            case PACKET_SUB_TYPE.ENABLE_FILERECORDER_V2: {
                mBytes.set(Uint16Bytes(this.portId!),8);
                mBytes[10] = this.enableRecorder!?0x01:0x00;
                mBytes.set(Uint32Bytes(this.timeoutMs!), 11);
                mBytes.set(Uint32Bytes(this.timeoutFilesize!), 15);
                mBytes[19] = this.timeoutAction!;
                const encoder = new TextEncoder();
                const recordNameStrBytes = encoder.encode(this.recordName!)
                mBytes.set(recordNameStrBytes, 20)
                break;
            }
            case PACKET_SUB_TYPE.ACTIVATE_ROLE: {
                mBytes[8] = this.accessControlRole!
                const encoder = new TextEncoder();
                const pwdStrBytes = encoder.encode(this.password!)

                mBytes.set(pwdStrBytes, 9)
                break;
            }
            case PACKET_SUB_TYPE.DIR_READ_NEXT: {
                mBytes[8] = this.driveIdx!;
                break;
            }
            case PACKET_SUB_TYPE.DIR_OPEN: {
                mBytes[8] = this.driveIdx!;
                const encoder = new TextEncoder();
                const dirPathStr = encoder.encode(this.dirPath!);
                mBytes.set(dirPathStr, 9);
                break;
            }
            case PACKET_SUB_TYPE.FILE_OPEN: {
                mBytes[8] = this.driveIdx!;
                mBytes[9] = this.fileAccessType!;
                mBytes[10] = this.fileAccessMode!;

                const encoder = new TextEncoder();
                const filePathStr = encoder.encode(this.filePath!);
                mBytes.set(filePathStr, 11);
                break;
            }
            case PACKET_SUB_TYPE.FILE_CLOSE: {
                mBytes[8] = this.driveIdx!;
                mBytes.set(Uint16Bytes(this.fileIdx!), 9);
                break;
            }
            case PACKET_SUB_TYPE.GET_FILE_HASH: {
                mBytes[8] = this.driveIdx!;
                mBytes.set(Uint16Bytes(this.fileIdx!), 9);
                mBytes.set(Uint32Bytes(this.numBytesToHash!), 11);
                mBytes.set(Uint32Bytes(this.maxRxSize!), 15);
                break;
            }
            case PACKET_SUB_TYPE.GET_FILE_DATA: {
                mBytes[8] = this.driveIdx!;
                mBytes.set(Uint16Bytes(this.fileIdx!), 9);
                mBytes.set(Uint32Bytes(this.startAddress!), 11);
                mBytes.set(Uint32Bytes(this.maxRxSize!), 15);
                break;
            }
            case PACKET_SUB_TYPE.FILE_REMOVE: {
                mBytes[8] = this.driveIdx!;
                const encoder = new TextEncoder();
                const filePathStr = encoder.encode(this.filePath!);
                mBytes.set(filePathStr, 9);
                break;
            }
            case PACKET_SUB_TYPE.REFORMAT_DRIVE: {
                mBytes[8] = this.driveIdx!;
                break;
            }
            case PACKET_SUB_TYPE.FILE_WRITE: {
                mBytes[8] = this.driveIdx!;
                mBytes.set(Uint16Bytes(this.fileIdx!), 9);
                mBytes[11] = this.controlFlags!;
                mBytes.set(Uint32Bytes(this.startAddress!), 12);
                mBytes.set(Uint32Bytes(this.fileData!.length), 16);
                mBytes.set(Uint32Bytes(this.chunkHash!), 20);
                mBytes.set(this.fileData!, 24);
                break;
            }
            case PACKET_SUB_TYPE.IDENTIFY: {
                mBytes[8] = this.led_pattern!.red;
                mBytes[9] = this.led_pattern!.green;
                mBytes[10] = this.led_pattern!.blue;
                mBytes.set(Uint32Bytes(this.led_pattern!.durationUs), 11);
                mBytes.set(Uint32Bytes(this.led_pattern!.blinkRate), 15);
                break;
            }
            case PACKET_SUB_TYPE.GET_FILERECORDER_STATS: {
                mBytes.set(Uint16Bytes(this.portId!), 8);
                break;
            }
            case PACKET_SUB_TYPE.SET_COLOR: {
                mBytes[8] = this.led_color!.red;
                mBytes[9] = this.led_color!.green;
                mBytes[10] = this.led_color!.blue;
                break;
            }
            case PACKET_SUB_TYPE.DFU_GET_IMAGE_INFO: {
                mBytes[8] = this.imageIdx!;
                break;
            }
            case PACKET_SUB_TYPE.DFU_UPLOAD_IMAGE: {
                
                mBytes.set(Uint32Bytes(this.startAddress!), 8);
                mBytes.set(Uint32Bytes(this.fileData!.length), 12);
                mBytes.set(this.fileData!, 16);
                break;
            }
            // Handle other cases similarly
        }

        const crc = this.calcCRC16(mBytes, 0x00);
        const crcBytes = Uint16Bytes(crc);
        mBytes.set(crcBytes, 6);

        this.mBytes = mBytes
        return true;
    }

    payloadLenWords(subType: PACKET_SUB_TYPE | null): number {
        switch(subType) {
            case PACKET_SUB_TYPE.GET_TIME:
                return 0;
            case PACKET_SUB_TYPE.SUBSCRIBE_BY_ID:
                return 3;
            case PACKET_SUB_TYPE.TEST:
                return 1;
            case PACKET_SUB_TYPE.GET_PUBLISHERS:
                return 0;
            case PACKET_SUB_TYPE.GET_PUBLISHER_NAME:
                return 1;
            case PACKET_SUB_TYPE.GET_OUTPUTS:
                return 1;
            case PACKET_SUB_TYPE.GET_OUTPUT_NAME:
                return 1;
            case PACKET_SUB_TYPE.GET_OUTPUT_UNITS:
                return 1;
            case PACKET_SUB_TYPE.SET_TIME:
                return 1;
            case PACKET_SUB_TYPE.SET_DATE:
                return 1;
            case PACKET_SUB_TYPE.PORT_UNSUBSCRIBE_ALL:
                return 1;
            case PACKET_SUB_TYPE.SET_POWER:
                return 1;
            case PACKET_SUB_TYPE.GET_PARAMETERS:
                return 1;
            case PACKET_SUB_TYPE.GET_PARAMETER_SET_INFO:
                return 1;
            case PACKET_SUB_TYPE.GET_PARAMETER_INFO:
                return 1;
            case PACKET_SUB_TYPE.GET_PARAMETER_VAL:
                return 1;
            case PACKET_SUB_TYPE.SET_PARAMETER_VAL:
                return 1;
            case PACKET_SUB_TYPE.ACTIVATE_ROLE:
                return 1;
            case PACKET_SUB_TYPE.GET_DRIVE_LIST:
                return 0;
            case PACKET_SUB_TYPE.DIR_OPEN:
                return 1;
            case PACKET_SUB_TYPE.DIR_READ_NEXT:
                return 1;
            case PACKET_SUB_TYPE.FILE_OPEN:
                return 1;
            case PACKET_SUB_TYPE.FILE_CLOSE:
                return 1;
            case PACKET_SUB_TYPE.GET_FILE_HASH:
                return 3;
            case PACKET_SUB_TYPE.GET_FILE_DATA:
                return 3;
            case PACKET_SUB_TYPE.FILE_REMOVE:
                return 1;
            case PACKET_SUB_TYPE.FILE_WRITE:
                return 4;
            case PACKET_SUB_TYPE.REFORMAT_DRIVE:
                return 1;
            case PACKET_SUB_TYPE.IDENTIFY:
                return 3;
            case PACKET_SUB_TYPE.GET_FILERECORDER_STATS:
                return 1;
            case PACKET_SUB_TYPE.SET_COLOR:
                return 1;
            case PACKET_SUB_TYPE.DFU_GET_IMAGE_INFO:
                return 1;
            case PACKET_SUB_TYPE.DFU_UPLOAD_IMAGE:
                return 2;
            case PACKET_SUB_TYPE.GET_SYSTEM_STATUS:
                return 0;
            default:
                return 0;
        }
    }



    getLoggingString(): string {
        let pktMsg = "";
        switch (this.pktSubType) {
            case PACKET_SUB_TYPE.NA:
                pktMsg = "";
                break;
            case PACKET_SUB_TYPE.TEST:
                pktMsg = "";
                break;
            case PACKET_SUB_TYPE.SUBSCRIBE_BY_ID:
                pktMsg = `PUB_ID: ${this.publisherId}  PORT_ID: ${this.destinationPortID} Downsample: ${this.downSample}`;
                break;
            case PACKET_SUB_TYPE.GET_PUBLISHER_NAME:
                pktMsg = `PUB_ID: ${this.publisherId}`;
                break;
            case PACKET_SUB_TYPE.GET_OUTPUTS:
                pktMsg = `PUB_ID: ${this.publisherId}`;
                break;
            case PACKET_SUB_TYPE.GET_OUTPUT_NAME:
                pktMsg = `PUB_ID: ${this.publisherId}  OUTPUT_IDX: ${this.outputIdx}`;
                break;
            // Handle other cases similarly
        }

        return `[${this.mPacketType}][${this.pktSubType} #${this.mPacketNumber}]: ${pktMsg}`;
    }
}

